import styled from '@grebban/style-system-react';
import filters from 'config/algolia/filters';
import sorts from 'config/algolia/sorts';
import { useRef, useState } from 'react';
import FilterContainer from '@sportson/core-web/containers/FilterContainer';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import useSaveResponse from '@sportson/core-web/hooks/useSaveResponse';
import BrandHeader from 'components/Brand/BrandHeader';
import CategoryModule from 'components/Category/Category';
import { transformWordpressProductSelectionToAlgoliaParameters } from 'libs/wordpress/utils/transformWordpressProductSelectionToAlgoliaParameters';
import { shallowEqual } from 'react-redux';
import CmsModules from '@sportson/core-web/libs/wordpress/content/CmsModules';
import Pagination from 'components/Pagination';
import { inServer } from '@sportson/core-web/constants';
import convertCmsFiltersToFilter from '@sportson/core-web/utils/convertCmsFiltersToFilter';

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
`;

const Brand = () => {
    const {
        breadcrumbs,
        title,
        settings: { description },
    } = useAppSelector(({ page }) => page?.data, shallowEqual);
    const pageContent = useAppSelector(({ page }) => page?.data?.pageContent, shallowEqual);
    const childrens = useAppSelector(({ page }) => page?.data?.children, shallowEqual);
    const wrapperRef = useRef({});

    const productFilters = useAppSelector(
        ({ page }) => page?.data?.productGrid?.productGrid?.productFilters,
        shallowEqual,
    );

    const productSelection = useAppSelector(
        ({ page }) => page?.data?.productGrid.productGrid.productSelection,
        shallowEqual,
    );

    const cmsSelection = transformWordpressProductSelectionToAlgoliaParameters(productSelection)?.default || {};
    const cmsFilters = convertCmsFiltersToFilter(productFilters);
    const mergedFilters = { ...filters, ...cmsFilters };

    // Save response from container
    const [savedResponse, saveResponse] = useSaveResponse();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const productsPerPage = 20;

    const scrollToTop = (next, smooth = false) => {
        // Scroll back to top of wrapper on pagination
        const headerHeight =
            (!inServer && getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

        setCurrentPage(next);
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    const breadcrumbData = breadcrumbs.map((b) => ({ permalink: b.permalink, title: b.title }));
    breadcrumbData.push({ permalink: null, title });

    return (
        <Wrapper>
            <BrandHeader
                title={title}
                description={description}
                breadcrumbs={breadcrumbData}
                subCategories={childrens}
            />
            <FilterContainer
                pageSize={productsPerPage}
                pagination={currentPage}
                config={mergedFilters}
                filters={cmsSelection}
                sorts={sorts}
                render={(props) => {
                    const { response } = props;
                    saveResponse(response);
                    response && setPageCount(response.pageCount);
                    response && setCurrentPage(response.page);

                    return (
                        savedResponse.current.products && (
                            <CategoryModule config={mergedFilters} response={savedResponse.current} />
                        )
                    );
                }}
            />
            {pageCount && (
                <Pagination
                    handleClick={scrollToTop}
                    page={currentPage}
                    pageCount={pageCount}
                    pageSize={productsPerPage}
                />
            )}
            {pageContent && <CmsModules data={pageContent} />}
        </Wrapper>
    );
};

export default Brand;
